import {
	type AccountInfo,
	CacheLookupPolicy,
	InteractionRequiredAuthError,
	type IPublicClientApplication,
} from "@azure/msal-browser";
import {
	type AuthenticationOutcome,
	type AuthHost,
	getAuthErrorCode,
	hasRequireLoginError,
	isCancelledAuthError,
} from "./auth-errors";
import { createLoginRequest } from "./login-request";
import { getSilentRedirectUri } from "./silent-redirect";
import { syncActiveAccount } from "./sync-active-account";

const SSO_SCOPES = ["User.Read"] as const;

export interface InitializeAuthenticationInput {
	readonly configuredScopes: string;
	readonly host: AuthHost;
	readonly instance: IPublicClientApplication;
	readonly loginHint?: string | null;
	readonly onBeforeInteractive?: () => void;
	/** Teams tab SSO. Resolves an account without any iframe or popup. */
	readonly resolveHostAccount?: () => Promise<AccountInfo | null>;
}

const tryAcquireMeSilent = async (instance: IPublicClientApplication, account: AccountInfo): Promise<boolean> => {
	try {
		await instance.acquireTokenSilent({
			account,
			scopes: [...SSO_SCOPES],
			redirectUri: getSilentRedirectUri(),
			cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
		});
		return true;
	} catch (error: unknown) {
		const code = getAuthErrorCode(error);
		if (hasRequireLoginError(code) || error instanceof InteractionRequiredAuthError) {
			return false;
		}
		throw error;
	}
};

const trySsoSilent = async (instance: IPublicClientApplication, loginHint?: string | null): Promise<boolean> => {
	try {
		await instance.ssoSilent({
			scopes: [...SSO_SCOPES],
			redirectUri: getSilentRedirectUri(),
			...(loginHint ? { loginHint } : {}),
		});
		return syncActiveAccount(instance);
	} catch (error: unknown) {
		const code = getAuthErrorCode(error);
		if (hasRequireLoginError(code) || error instanceof InteractionRequiredAuthError) {
			return false;
		}
		console.debug("ssoSilent failed", error);
		return false;
	}
};

const redirectToInteractive = async (input: InitializeAuthenticationInput): Promise<AuthenticationOutcome> => {
	const { instance, configuredScopes, host, onBeforeInteractive } = input;

	onBeforeInteractive?.();

	if (host === "teams") {
		return { kind: "guest" };
	}

	await instance.loginRedirect(createLoginRequest(configuredScopes));
	return { kind: "redirecting" };
};

/**
 * MSAL bootstrap: redirect handle -> cache/RT silent -> Teams host SSO -> SSO
 * iframe (no account) -> interactive. Teams host never uses loginRedirect.
 */
export const initializeAuthentication = async (
	input: InitializeAuthenticationInput
): Promise<AuthenticationOutcome> => {
	const { instance, host, loginHint, resolveHostAccount } = input;

	try {
		await instance.handleRedirectPromise();
	} catch (error: unknown) {
		if (!(syncActiveAccount(instance) || isCancelledAuthError(error))) {
			throw error;
		}
	}

	const tryHostAccount = async (): Promise<AuthenticationOutcome | null> => {
		if (host !== "teams" || !resolveHostAccount) {
			return null;
		}
		const hostAccount = await resolveHostAccount();
		return hostAccount ? { kind: "authenticated", account: hostAccount } : null;
	};

	if (syncActiveAccount(instance)) {
		const account = instance.getActiveAccount();
		if (account && (await tryAcquireMeSilent(instance, account))) {
			return { kind: "authenticated" };
		}
		// Cached account but silent/RT failed (MFA / session). Skip ssoSilent iframe noise.
		if (account) {
			return (await tryHostAccount()) ?? (await redirectToInteractive(input));
		}
	}

	const hostOutcome = await tryHostAccount();
	if (hostOutcome) {
		return hostOutcome;
	}

	const ssoOk = await trySsoSilent(instance, host === "teams" ? loginHint : null);
	if (ssoOk) {
		const account = instance.getActiveAccount();
		if (account && (await tryAcquireMeSilent(instance, account))) {
			return { kind: "authenticated" };
		}
	}

	// SSO failed but cache may still have an account worth one more silent try.
	if (syncActiveAccount(instance)) {
		const account = instance.getActiveAccount();
		if (account && (await tryAcquireMeSilent(instance, account))) {
			return { kind: "authenticated" };
		}
	}

	return redirectToInteractive(input);
};
